/* eslint func-style: ["error", "declaration"] */
/* eslint no-use-before-define: ["error", { "functions": false }] */

import get from 'lodash/get';
import range from 'lodash/range';
import set from 'lodash/set';
import isEmpty from 'lodash/isEmpty';
import { ARRAY_SELECTION_WIDGETS } from 'lib/const';

/**
 * get defaults for a contract section
 * @param {object} section - a contract section object
 * @param {boolean} getDefaultChildren - true if default children is required (overrides defaultArrayLength prop)
 * @returns {object} a default object for the given section
 */
export function getDefaultsFromSection(section, getDefaultChildren) {
  const { propLocation = 'Payload' } = section;
  return get(
    makeDefaultsFromSection(section, getDefaultChildren),
    propLocation.replace(/indexValue[0-9]/g, 0),
    {}
  );
}

/**
 * create an object containing defaults based on a contract section
 * @param {object} section - a canvas contract section object
 * @param {Array} [section.children = []] - an array of children section objects
 * @param {string} [section.defaultValue = ''] - defaultValue for section object (field only)
 * @param {string} [section.propLocation = ''] - propLocation for section object (field only)
 * @param {string} [section.type = ''] - type of section object
 * @param {boolean} getDefaultChildren - true if default children is required (overrides defaultArrayLength prop)
 * @param {object} obj - an initial object for defaults
 * @returns {object} object with defaults
 */
export function makeDefaultsFromSection(
  {
    canvasWidget,
    children = [],
    defaultArrayLength = 1,
    defaultValue = canvasWidget === 'AttachChildInput' ? [] : '',
    defaultValues = [],
    propLocation = '',
    type = ''
  },
  getDefaultChildren = false,
  obj = {}
) {
  if (type === 'array' && defaultArrayLength === 0 && !getDefaultChildren) {
    return set(obj, propLocation.replace(/indexValue[0-9]/g, 0), []);
  }

  return type === 'field'
    ? set(
        obj,
        propLocation.replace(/indexValue[0-9]/g, 0),
        ARRAY_SELECTION_WIDGETS.includes(canvasWidget) ? defaultValues : defaultValue
      )
    : children.reduce(
        (acc, cur) => makeDefaultsFromSection(cur, getDefaultChildren, acc),
        obj
      );
}

/**
 * make form component collapse state based on contract and existing props
 * @param {object} section - a canvas contract section object
 * @param {Array} [section.children = []] - an array of children section objects
 * @param {object} props - props used to create object elements for array section state
 * @param {number} indexValue - value used to replace /indexValue[0-9]/ for array state
 * @param {boolean} open - initial state for all sections
 * @param {object} emptyInitialState - base object for returned state
 * @returns {object} collapse state
 */
export function makeCollapseState(
  { children = [] },
  props = {},
  indexValue = 0,
  open = false,
  emptyInitialState = true
) {
  return makeStateFromChildren(children, props, indexValue, open, emptyInitialState);
}

/**
 * make state based on some children
 * @param {Array} children - an array of canvas section objects
 * @param {object} props - existing props used for array section state
 * @param {number} indexValue - value used to replace /indexValue[0-9]/ for array state
 * @param {boolean} open - initial state for all sections
 * @param {boolean} emptyInitialState - use an empty object as the initial state
 * @returns {object} state
 */
export function makeStateFromChildren(
  children,
  props,
  indexValue,
  open,
  emptyInitialState = false
) {
  const state = emptyInitialState ? {} : { open };
  children.forEach(child => {
    state[child.name] = makeStateFromSection(child, props, indexValue, open);
  });
  return state;
}

/**
 * make state from a section object
 * @param {object} section - a canvas contract section object
 * @param {Array} [section.children = []] - an array of children section objects
 * @param {string} [section.propLocation = ''] - path within props for current section (array only)
 * @param {string} [section.type = 'object'] - type of section object
 * @param {object} props - existing props used for array section state
 * @param {number} indexValue - value used to replace /indexValue[0-9]/ for array state
 * @param {boolean} open - initial state for all sections
 * @returns {object} state
 */
export function makeStateFromSection(
  { children = [], propLocation = '', type = 'object' },
  props = {},
  indexValue = 0,
  open = false
) {
  // ignore children with type field because individual fields do not get state
  const filteredChildren = children.filter(({ type }) => type !== 'field');
  if (type === 'object') {
    return makeStateFromChildren(filteredChildren, props, indexValue, open);
  }

  // base state for array sections on existing length of props or fallback to single item
  const { length } = get(props, propLocation.replace(/indexValue[0-9]/, indexValue), [0]);
  return {
    open,
    items: range(length).map(i => makeStateFromChildren(filteredChildren, props, i, open))
  };
}

/**
 * Get all children with  provided canvasWidget value and pull propLocation data.
 * @param {object} obj - canvas contract object
 * @param {string} widget - canvas widget
 * @returns {string[]} - a list of propLocations
 */
export function getPropLocation(obj = {}, widget = '') {
  const propLocation = [];
  if (obj.type === 'field' && obj.canvasWidget === widget) {
    return [obj.propLocation];
  } else if (obj.children) {
    for (let i = 0; i < obj.children.length; i++) {
      const loc = getPropLocation(obj.children[i], widget);
      if (!isEmpty(loc)) {
        propLocation.push(...loc);
      }
    }
  }
  return propLocation;
}

/**
 * evaluate a contract string
 * @param {string} str - contract string
 * @returns {object} contract object
 */
export function evalContractString(str) {
  // eslint-disable-next-line no-new-func
  const setContract = new Function('module', str);
  const obj = {};
  setContract(obj);
  return obj.exports;
}
